import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Network, RefreshCw, Search, ChevronDown, ChevronUp,
  Check, X, Power, PowerOff, Cpu, Activity, WifiOff
} from 'lucide-react';
import { tracksolidApi } from '../../apis/tracksolidApi';
import { toast } from 'react-toastify';

interface Subcuenta {
  id: number;
  tracksolid_account: string;
  subcuenta: string;
  plataforma_id: number;
  activa: boolean;
  ultima_sincronizacion: string | null;
  plataforma?: { id: number; nombre: string; slug: string };
}

const Subcuentas: React.FC = () => {
  const queryClient = useQueryClient();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [dispositivos, setDispositivos] = useState<Record<string, any[]>>({});
  const [loadingDevices, setLoadingDevices] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: subcuentas = [], isLoading, refetch } = useQuery<Subcuenta[]>({
    queryKey: ['subcuentas'],
    queryFn: tracksolidApi.getSubcuentas,
  });

  const toggleMutation = useMutation({
    mutationFn: (sub: Subcuenta) =>
      tracksolidApi.guardarSubcuenta({
        tracksolid_account: sub.tracksolid_account,
        subcuenta: sub.subcuenta,
        plataforma_id: sub.plataforma_id,
        activa: !sub.activa,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcuentas'] });
      toast.success('Subcuenta actualizada');
    },
    onError: () => toast.error('Error al actualizar'),
  });

  const syncMutation = useMutation({
    mutationFn: () => tracksolidApi.sincronizar('skyangelmonitoreo'),
    onSuccess: (data) => {
      toast.success(`Sincronización completada: ${data.tags_nuevas || 0} nuevas, ${data.tags_actualizadas || 0} actualizadas`);
    },
    onError: () => toast.error('Error al sincronizar'),
  });

  const loadDispositivos = async (subcuenta: string) => {
    if (expandedRow === subcuenta) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(subcuenta);
    if (dispositivos[subcuenta]) return;

    setLoadingDevices(subcuenta);
    try {
      const res = await tracksolidApi.getDispositivosSubcuenta(subcuenta);
      setDispositivos(prev => ({ ...prev, [subcuenta]: res.data || [] }));
    } catch {
      toast.error('Error al cargar dispositivos');
    } finally {
      setLoadingDevices(null);
    }
  };

  const filtered = subcuentas.filter(s =>
    !searchTerm ||
    s.subcuenta.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.plataforma?.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activas = subcuentas.filter(s => s.activa).length;
  const inactivas = subcuentas.length - activas;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-300">Cargando subcuentas...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .subcuentas-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
            color: white;
            padding: 2rem;
          }

          .content-wrapper {
            max-width: 1400px;
            margin: 0 auto;
          }

          .page-header {
            text-align: left;
            margin-bottom: 3rem;
          }

          .page-title {
            font-size: 2.5rem;
            font-weight: 700;
            background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
          }

          .page-subtitle {
            color: rgba(255, 255, 255, 0.7);
            font-size: 1.1rem;
          }

          .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
            margin-bottom: 2rem;
          }

          .stat-card {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
            transition: all 0.3s ease;
          }

          .stat-card:hover {
            background: rgba(255, 255, 255, 0.08);
            border-color: rgba(0, 122, 255, 0.3);
            transform: translateY(-2px);
          }

          .stat-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 1rem;
          }

          .stat-title {
            color: rgba(255, 255, 255, 0.8);
            font-size: 0.9rem;
            font-weight: 500;
          }

          .stat-value {
            font-size: 2rem;
            font-weight: 700;
            color: white;
          }

          .controls-section {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
            margin-bottom: 2rem;
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            align-items: center;
            justify-content: space-between;
          }

          .search-container {
            position: relative;
            flex: 1;
            max-width: 400px;
          }

          .search-input {
            width: 100%;
            padding: 12px 16px 12px 48px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            color: white;
            font-size: 1rem;
            transition: all 0.3s ease;
          }

          .search-input:focus {
            outline: none;
            background: rgba(255, 255, 255, 0.15);
            border-color: #007AFF;
            box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
          }

          .search-input::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }

          .search-icon {
            position: absolute;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            color: rgba(255, 255, 255, 0.5);
          }

          .action-buttons {
            display: flex;
            gap: 1rem;
          }

          .btn {
            padding: 12px 20px;
            border-radius: 12px;
            font-weight: 500;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
            border: none;
            cursor: pointer;
            font-size: 1rem;
          }

          .btn-primary {
            background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
            color: white;
          }

          .btn-primary:hover {
            background: linear-gradient(135deg, #0056CC 0%, #003D99 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 122, 255, 0.3);
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .btn-secondary {
            background: rgba(255, 255, 255, 0.1);
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .btn-secondary:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          .table-container {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            overflow: hidden;
          }

          .table {
            width: 100%;
            border-collapse: collapse;
          }

          .table th {
            background: rgba(255, 255, 255, 0.1);
            padding: 1rem;
            text-align: left;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.9);
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .table td {
            padding: 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          }

          .table tr:hover td {
            background: rgba(255, 255, 255, 0.05);
          }

          .badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 500;
            border: 1px solid;
          }

          .badge-green {
            background: rgba(52, 199, 89, 0.2);
            color: #34C759;
            border-color: rgba(52, 199, 89, 0.3);
          }

          .badge-red {
            background: rgba(255, 59, 48, 0.2);
            color: #FF3B30;
            border-color: rgba(255, 59, 48, 0.3);
          }

          .badge-blue {
            background: rgba(0, 122, 255, 0.2);
            color: #007AFF;
            border-color: rgba(0, 122, 255, 0.3);
          }

          .action-btn {
            padding: 8px 12px;
            border-radius: 8px;
            border: none;
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.7);
            cursor: pointer;
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 0.8rem;
          }

          .action-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            color: white;
          }

          .expand-btn {
            padding: 6px;
            border-radius: 6px;
            border: none;
            background: rgba(255, 255, 255, 0.08);
            color: rgba(255, 255, 255, 0.5);
            cursor: pointer;
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
          }

          .expand-btn:hover {
            background: rgba(255, 255, 255, 0.15);
            color: white;
          }

          .devices-panel {
            background: rgba(0, 122, 255, 0.05);
            border-top: 1px solid rgba(0, 122, 255, 0.15);
            padding: 1.25rem;
          }

          .devices-panel-title {
            font-size: 0.8rem;
            color: rgba(255, 255, 255, 0.5);
            margin-bottom: 0.75rem;
          }

          .device-chip {
            display: inline-block;
            background: rgba(255, 255, 255, 0.08);
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 8px;
            padding: 6px 12px;
            margin: 4px;
            font-size: 0.8rem;
            color: rgba(255, 255, 255, 0.8);
            transition: all 0.2s ease;
          }

          .device-chip:hover {
            background: rgba(0, 122, 255, 0.15);
            border-color: rgba(0, 122, 255, 0.3);
          }

          .empty-state {
            text-align: center;
            padding: 4rem 2rem;
            color: rgba(255, 255, 255, 0.6);
          }

          .results-info {
            text-align: center;
            color: rgba(255, 255, 255, 0.6);
            font-size: 0.875rem;
            margin-top: 1rem;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
        `
      }} />

      <div className="subcuentas-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="page-header">
            <h1 className="page-title">Subcuentas Tracksolid</h1>
            <p className="page-subtitle">Gestiona el mapeo de subcuentas de Tracksolid a plataformas del sistema</p>
          </div>

          {/* Estadísticas */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-title">Total Subcuentas</span>
                <Network size={20} color="#007AFF" />
              </div>
              <div className="stat-value">{subcuentas.length}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-title">Activas</span>
                <Activity size={20} color="#34C759" />
              </div>
              <div className="stat-value">{activas}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-title">Inactivas</span>
                <WifiOff size={20} color="#FF3B30" />
              </div>
              <div className="stat-value">{inactivas}</div>
            </div>
          </div>

          {/* Controles */}
          <div className="controls-section">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Buscar subcuenta o plataforma..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="action-buttons">
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="btn btn-secondary">
                  Limpiar
                </button>
              )}

              <button onClick={() => refetch()} className="btn btn-secondary">
                <RefreshCw size={16} />
                Actualizar
              </button>

              <button
                className="btn btn-primary"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
              >
                <RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />
                {syncMutation.isPending ? 'Sincronizando...' : 'Sincronizar Tags'}
              </button>
            </div>
          </div>

          {/* Tabla */}
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Subcuenta</th>
                  <th>Plataforma</th>
                  <th>Cuenta Principal</th>
                  <th>Estado</th>
                  <th>Última Sincronización</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(sub => (
                  <React.Fragment key={sub.id}>
                    <tr>
                      <td>
                        <button
                          className="expand-btn"
                          onClick={() => loadDispositivos(sub.subcuenta)}
                        >
                          {loadingDevices === sub.subcuenta ? (
                            <RefreshCw size={14} className="animate-spin" />
                          ) : expandedRow === sub.subcuenta ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )}
                        </button>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '10px',
                            background: 'rgba(0, 122, 255, 0.15)', border: '1px solid rgba(0, 122, 255, 0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <Cpu size={18} color="#007AFF" />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'white' }}>{sub.subcuenta}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-blue">{sub.plataforma?.nombre || '—'}</span>
                      </td>
                      <td style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{sub.tracksolid_account}</td>
                      <td>
                        <span className={`badge ${sub.activa ? 'badge-green' : 'badge-red'}`}>
                          {sub.activa ? <Check size={12} /> : <X size={12} />}
                          {sub.activa ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                        {sub.ultima_sincronizacion
                          ? new Date(sub.ultima_sincronizacion).toLocaleString('es-MX')
                          : 'Nunca'}
                      </td>
                      <td>
                        <button
                          className="action-btn"
                          onClick={() => toggleMutation.mutate(sub)}
                          disabled={toggleMutation.isPending}
                          title={sub.activa ? 'Desactivar subcuenta' : 'Activar subcuenta'}
                        >
                          {sub.activa
                            ? <><PowerOff size={14} color="#FF3B30" /> Desactivar</>
                            : <><Power size={14} color="#34C759" /> Activar</>
                          }
                        </button>
                      </td>
                    </tr>
                    {expandedRow === sub.subcuenta && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <div className="devices-panel">
                            <div className="devices-panel-title">
                              Dispositivos en esta subcuenta
                              {dispositivos[sub.subcuenta] && ` (${dispositivos[sub.subcuenta].length})`}
                            </div>
                            {!dispositivos[sub.subcuenta] ? (
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>Cargando...</span>
                            ) : dispositivos[sub.subcuenta].length === 0 ? (
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>Sin dispositivos asignados</span>
                            ) : (
                              <div>
                                {dispositivos[sub.subcuenta].map((d: any) => (
                                  <span key={d.imei} className="device-chip">
                                    {d.deviceName || d.imei}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        <Search size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 500, color: 'white', marginBottom: '0.5rem' }}>
                          No se encontraron subcuentas
                        </h3>
                        <p>Intenta ajustar el término de búsqueda</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="results-info">
              Mostrando {filtered.length} de {subcuentas.length} subcuentas
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Subcuentas;
